import React, { FC, useState, useEffect, useContext } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import styled, { css } from 'styled-components';
import isBefore from 'date-fns/isBefore';
import { Spacer } from '../Spacer';
import { Input } from '../forms/Input';
import { Typography } from '../Typography';
import { isValidDate } from '../../utils/isValidDate';
import { Panel } from '../Panel';
import { Button } from '../Button';
import { PanelGroup } from '../PanelGroup';
import { Checkbox } from '../forms/Checkbox';
import { currency } from '../../utils';
import HtmlContent from '../Html';
import {
  StyledTableCell,
  StyledTableHeaderCell,
  StyledTableFooterCell,
  StyledTableRow,
  StyledScrollableTable,
  Table,
} from '../Table';
import { UseStampDutyCalculator } from '../../hooks/useStampDutyCalculator';
import { Alert } from '../Alert';
import { getTodaysDate } from '../../utils/getTodaysDate';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { Loader } from '../Loader';
import { UiStateContext } from '../../context/UiStateContext';
import { CTACard } from '../CtaCard';
import { RequestAValuationBlade } from '../properties/RequestAValuationBlade';
import { useExperiments } from '../../context/ExperimentsContext';
import { useVariation } from '../../hooks/useVariation';

const FORM_ID = 'stamp-duty';

// Interfaces

export interface StampDutyCalculatorWithBreakdownProps {
  price?: number;
}

// Styles

const StyledInlineFields = styled.div`
  ${({ theme: { space, media } }) => css`
    display: grid;
    gap: ${space.md};

    & + & {
      margin-top: ${space.lg};
    }

    @media (min-width: ${media.md}) {
      display: flex;
      align-items: center;
      gap: ${space.lg};

      > * {
        flex-basis: 50%;
      }
    }
  `};
`;

const StyledResults = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.md};

    @media (min-width: ${media.md}) {
      flex-direction: row;
      gap: ${space.lg};
    }
  `};
`;

const StyledResult = styled.div`
  ${({ theme: { space, borders } }) => css`
    flex-grow: 1;
    border-bottom: ${borders.default};
    padding-bottom: ${space.md};
    margin-bottom: ${space.md};
  `};
`;

// JSX

interface ResultProps {
  heading: string;
  intro?: string;
  result: string;
}

interface ResultsProps {
  results: any;
  activePurchasePrice: number | undefined;
  beforeMiniBudget: boolean;
  firstTimeBuyer: boolean;
  nonResident: boolean;
  isAnyTestVariation: boolean;
}

const Result: FC<ResultProps> = ({ heading, intro, result }) => (
  <Panel backgroundColor="grey1">
    <StyledResult>
      <Typography component="h3" variant="titleSmall">
        {heading}
      </Typography>
      {intro && (
        <Typography component="p" variant="bodySmall">
          {intro}
        </Typography>
      )}
    </StyledResult>
    <Typography component="p" variant="titleMedium">
      {currency(result.toString())}
    </Typography>
  </Panel>
);

const Results: FC<ResultsProps> = ({
  results,
  activePurchasePrice,
  beforeMiniBudget,
  firstTimeBuyer,
  nonResident,
  isAnyTestVariation,
}) => {
  const { setActiveBlade } = useContext(UiStateContext);

  return (
    <>
      {results && (
        <Spacer>
          {/* Results overview */}
          <StyledResults>
            <Result heading="Purchase price" result={activePurchasePrice} />
            <Result heading="Total SDLT due" intro="Main residence" result={results.totalDue} />
            {!firstTimeBuyer && (
              <Result heading="Total SDLT due" intro="Additional property" result={results.totalDueAdditional} />
            )}
          </StyledResults>

          {!nonResident && (
            <Typography component="p" variant="smallprint">
              <strong>Note:</strong> First-time buyers will not pay any stamp duty on the first{' '}
              {beforeMiniBudget ? '£425,000' : '£300,000'} and will pay a reduced rate of 5% on the portion from{' '}
              {beforeMiniBudget ? '£425,001' : '£300,001'} to {beforeMiniBudget ? '£625,000' : '£500,000'}
            </Typography>
          )}

          {/* Show CTACard only if NOT (in test variation AND first time buyer) */}
          {!(isAnyTestVariation && firstTimeBuyer) && (
            <CTACard
              title="Need a valuation?"
              lead="Our local agents can provide an accurate valuation for your property."
              backgroundColour="brand"
              inlineButton
              customButton={{
                label: 'Request a valuation',
                onClick: () => setActiveBlade('propertyRequestAValuation'),
                eventClickBladeId: 'propertyRequestAValuation',
              }}
            />
          )}
          <RequestAValuationBlade />

          {/* Results breakdown */}
          <HtmlContent>
            <h3>SDLT breakdown</h3>
            <p>This is a breakdown of how the total amount of SDLT was calculated.</p>
          </HtmlContent>
          <StyledScrollableTable>
            <Table>
              <thead>
                <StyledTableRow>
                  <StyledTableHeaderCell>&nbsp;</StyledTableHeaderCell>
                  <StyledTableHeaderCell colSpan="2" stacked>
                    Main residence
                  </StyledTableHeaderCell>
                  {!firstTimeBuyer && (
                    <StyledTableHeaderCell colSpan="2" stacked>
                      Additional property
                    </StyledTableHeaderCell>
                  )}
                </StyledTableRow>
              </thead>
              <thead>
                <StyledTableRow>
                  <StyledTableHeaderCell>Purchase price bands</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Rate</StyledTableHeaderCell>
                  <StyledTableHeaderCell>SDLT due (£)</StyledTableHeaderCell>
                  {!firstTimeBuyer && (
                    <>
                      <StyledTableHeaderCell>Rate</StyledTableHeaderCell>
                      <StyledTableHeaderCell>SDLT due</StyledTableHeaderCell>
                    </>
                  )}
                </StyledTableRow>
              </thead>
              <tbody>
                {results?.bands.map((band, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell wrap>{band.name}</StyledTableCell>
                    <StyledTableCell>{band.rate}%</StyledTableCell>
                    <StyledTableCell>{band.due === 0 ? '£0' : currency(band.due)}</StyledTableCell>
                    {!firstTimeBuyer && (
                      <>
                        <StyledTableCell>{band.rateAdditional}%</StyledTableCell>
                        <StyledTableCell>
                          {band.dueAdditional === 0 ? '£0' : currency(band.dueAdditional)}
                        </StyledTableCell>
                      </>
                    )}
                  </StyledTableRow>
                ))}
                <StyledTableRow>
                  <StyledTableFooterCell />
                  <StyledTableFooterCell>Total due</StyledTableFooterCell>
                  <StyledTableFooterCell>{currency(results.totalDue)}</StyledTableFooterCell>
                  {!firstTimeBuyer && (
                    <>
                      <StyledTableFooterCell>Total due</StyledTableFooterCell>
                      <StyledTableFooterCell>{currency(results.totalDueAdditional)}</StyledTableFooterCell>
                    </>
                  )}
                </StyledTableRow>
              </tbody>
            </Table>
          </StyledScrollableTable>
        </Spacer>
      )}
    </>
  );
};

export const StampDutyCalculatorWithBreakdown: FC<StampDutyCalculatorWithBreakdownProps> = ({ ...rest }) => {
  const { stampDutyCalculatorResults, handleStampDutyCalculation, loading } = UseStampDutyCalculator();
  const [results, setResults] = useState();
  const [showFirstTimeBuyerResults, setShowFirstTimeBuyerResults] = useState(false);
  const [firstTimeBuyer, setFirstTimeBuyer] = useState(false);
  const [nonResident, setNonResident] = useState(false);
  const [firstTimeResults, setFirstTimeResults] = useState();
  const [activePurchasePrice, setActivePurchasePrice] = useState<number>();
  const [noResults, setNoResults] = useState<boolean>(false);
  const [beforeMiniBudget, setBeforeMiniBudget] = useState<boolean>(false);
  const { dataLayerPush } = useDataLayerPush();
  const { isAnyTestVariation } = useVariation('KFH25002');

  const formMethods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      nonUkResident: false,
      firstTimeBuyer: false,
      effectiveDate: getTodaysDate(),
      price: '',
    },
  });

  const { register, handleSubmit, control, formState } = formMethods;
  const { errors, dirtyFields, touchedFields } = formState;

  // Handle data change

  useEffect(() => {
    if (stampDutyCalculatorResults && stampDutyCalculatorResults.stampDuty.stampDutyCalculator.results.length > 0) {
      const activeResults = stampDutyCalculatorResults.stampDuty.stampDutyCalculator;
      setNoResults(false);
      setResults(activeResults.results[0]);
      setFirstTimeResults(activeResults.results[1]);
      setActivePurchasePrice(activeResults.price);
    } else if (
      stampDutyCalculatorResults &&
      stampDutyCalculatorResults.stampDuty.stampDutyCalculator.results.length === 0
    ) {
      setNoResults(true);
    }
  }, [stampDutyCalculatorResults]);

  // Function to handle collecting form data and  submitting the GQL mutation
  const onSubmit = (formData) => {
    const stringToDate = (_date, _format, _delimiter) => {
      const formatLowerCase = _format.toLowerCase();
      const formatItems = formatLowerCase.split(_delimiter);
      const dateItems = _date.split(_delimiter);
      const monthIndex = formatItems.indexOf('mm');
      const dayIndex = formatItems.indexOf('dd');
      const yearIndex = formatItems.indexOf('yyyy');
      let month = parseInt(dateItems[monthIndex]);
      month -= 1;
      const formatedDate = new Date(dateItems[yearIndex], month, dateItems[dayIndex]);
      return formatedDate;
    };

    const date = stringToDate(formData.effectiveDate, 'dd/MM/yyyy', '/');
    const miniBudget = stringToDate('31/03/2025', 'dd/MM/yyyy', '/');

    setBeforeMiniBudget(isBefore(date, miniBudget));

    // Clean up data for query

    // Date format
    const dateParts = formData.effectiveDate.split('/');
    const effectiveDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

    // Price format
    const price = parseFloat(formData.price);

    // Run calculation

    handleStampDutyCalculation(effectiveDate, price, formData.nonUkResident);

    // Set first time buyer for results

    setFirstTimeBuyer(!!formData.firstTimeBuyer);

    if (formData.firstTimeBuyer && !formData.nonUkResident) {
      setShowFirstTimeBuyerResults(true);
    } else {
      setShowFirstTimeBuyerResults(false);
    }

    if (formData.nonUkResident) {
      setNonResident(true);
    } else {
      setNonResident(false);
    }

    dataLayerPush({
      event: 'stampDutyCalculator',
      sdcFormStep: 'Calculate Stamp Duty',
      sdcPurchasePrice: price,
      sdcDateOfPurchase: effectiveDate,
      sdcUKResident: !formData.nonUkResident,
      formValidation: 'success',
      formError: undefined,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <Spacer spacing="double">
        <PanelGroup>
          {/* Calculator view */}

          <Panel
            as="form"
            onSubmit={handleSubmit(onSubmit)}
            method="post"
            noValidate
            id={FORM_ID}
            backgroundColor="white"
            border
            {...rest}
          >
            <StyledInlineFields>
              <Input
                type="number"
                invalid={!!errors.price}
                valid={touchedFields.price && dirtyFields.price && !errors.price}
                label="Purchase price"
                id="price"
                placeholder="e.g 450000"
                {...register('price', { valueAsNumber: true, required: true, min: 1 })}
                required
                prefix="£"
                hideAsterisk
              />
              <Input
                type="text"
                invalid={!!errors.effectiveDate}
                valid={touchedFields.effectiveDate && dirtyFields.effectiveDate && !errors.effectiveDate}
                label="Effective date of purchase"
                id="effectiveDate"
                placeholder="dd/mm/yyyy"
                {...register('effectiveDate', {
                  required: true,
                  validate: {
                    isValidFormat: (v) => {
                      const regex = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;
                      return regex.test(v) || 'Please enter a valid date (dd/mm/yyyy)';
                    },
                    isValidDate: (v) => isValidDate(v) || 'Please enter a valid date (dd/mm/yyyy)',
                  },
                })}
                required
                hideAsterisk
                errorMessage={errors.effectiveDate?.message}
              />
            </StyledInlineFields>

            <StyledInlineFields>
              <Spacer spacing="half">
                <Controller
                  name="firstTimeBuyer"
                  control={control}
                  render={({ field }) => <Checkbox label="I am a first-time buyer" id="firstTimeBuyer" {...field} />}
                />
                <Controller
                  name="nonUkResident"
                  control={control}
                  render={({ field }) => <Checkbox label="I am a non-UK resident" id="nonUkResident" {...field} />}
                />
              </Spacer>
              <Button type="submit" disableClickTracking form={FORM_ID}>
                Calculate Stamp Duty
              </Button>
            </StyledInlineFields>
          </Panel>

          {/* Results view */}

          <>
            {loading && (
              <Panel>
                <Loader centered />
              </Panel>
            )}
            {noResults && !loading ? (
              <Panel backgroundColor="white" border>
                <Alert
                  heading="Results based on SDLT rules before 4 December 2014."
                  text={`<p>You may be entitled to pay SDLT using the old rules if you exchanged contracts before 4 December 2014.
                  Find out more on the <a href="gov.uk">GOV.UK</a> pages.</p>`}
                />
              </Panel>
            ) : (
              ''
            )}
            {results && !noResults && !loading ? (
              <Panel
                heading="Your results"
                backgroundColor="white"
                border
                {...rest}
                headingVariant="titleLarge"
                headingComponent="h2"
                headingMargin="double"
              >
                <Results
                  results={firstTimeResults && firstTimeBuyer ? firstTimeResults : results}
                  activePurchasePrice={activePurchasePrice}
                  beforeMiniBudget={beforeMiniBudget}
                  firstTimeBuyer={firstTimeBuyer}
                  nonResident={nonResident}
                  isAnyTestVariation={isAnyTestVariation}
                />
              </Panel>
            ) : (
              ''
            )}
          </>
        </PanelGroup>
      </Spacer>
    </FormProvider>
  );
};
